"use client"

import * as React from "react"
import type { ChartRange } from "@/lib/collection-chart-range"
import type { DistributionBin } from "@/lib/collection-category-stats"

/**
 * FRIDGE-1023 (owner, 09/15/2026): "For the data that shows on collection
 * pages — include fully interactive charts."
 *
 * The interactive layer of the "{Collection} by the Numbers" band: the
 * distribution of the collection's members over tracked price, and over
 * capacity, as one-series column charts. Every figure is the page's own
 * member data (lib/collection-category-stats.ts buildBins); nothing is typed
 * in or smoothed.
 *
 * Interaction (dataviz method): the mark is the hit target — hover or keyboard
 * focus on a column shows a tooltip with the bin's range, count and share, and
 * the column lifts; arrow keys move between columns; a Price / Capacity switch
 * swaps the measure; a table view carries every value without hovering.
 * Marks: <= 24 px columns, 4 px rounded caps, square at the baseline, a 2 px
 * surface gap between neighbours, hairline gridlines, one direct label on the
 * tallest column. One series ⇒ one hue (brand blue), no legend box.
 *
 * Real-or-Hidden: renders nothing without at least one measure carrying two or
 * more bins. Server HTML carries the SVG and the table (this is a client
 * component only for the hover/keyboard state).
 *
 * FRIDGE-1231 (owner, 09/24/2026: "say under $2,000 not 0" / "also i thought
 * other chart type visuals as well"): the first x-axis label now reads the
 * same "Under $2,000" / "Under 5 cu ft" as the table, tooltip and aria-label,
 * and a Chart type switch adds a Share donut and a Cumulative line — both fed
 * by the same bins, with the same keyboard and tooltip behaviour as the
 * columns. Tick labels are placed from their estimated width so none collides
 * or leaves the plot at any width.
 *
 * FRIDGE-1687: each SVG names itself with aria-label and describes itself with
 * <desc>, never with <title>. On the dynamic ?page=N route Next streams the
 * page's own <title> into the body, and a crawler that reads the first <title>
 * element took this chart's ("<Collection> — models by price") as the page
 * title. tests/svg-title-before-page-title-fridge-1687.test.ts keeps every
 * component a dynamic route renders free of an SVG <title>.
 */

const BAR = "#0469f0"
const BAR_HOVER = "#0454c0"
const INK = "#101828"
const MUTED = "#6a7282"
const RULE = "#e3e8ef"

/** Server-render width; the client measures the wrapper and re-lays out so
 *  text stays 11 px CSS at every width instead of scaling with a viewBox. */
const DEFAULT_W = 640
const MIN_W = 300
const VB_H = 200
const PAD_L = 44
const PAD_R = 12
const PAD_T = 22
const PAD_B = 34
const MAX_BAR = 24
const GAP = 2

type Measure = "price" | "capacity"
export type ChartType = "columns" | "donut" | "cumulative"

const CHART_TYPES: Array<{ id: ChartType; label: string }> = [
  { id: "columns", label: "Columns" },
  { id: "donut", label: "Share" },
  { id: "cumulative", label: "Cumulative" },
]

interface Props {
  title: string
  priceBins: DistributionBin[]
  capacityBins: DistributionBin[]
  totalModels: number
  /** Server-render starting view; defaults to columns (tests render each). */
  initialChartType?: ChartType
  initialMeasure?: Measure
  /**
   * FRIDGE-1278 — when present, every section (column, slice, point, legend
   * row, table row) is a control: choosing it filters the models listed below
   * to that range, choosing it again clears it.
   */
  onSelectRange?: (range: ChartRange | null) => void
  /** The range currently applied to the listing, so the chart shows it. */
  selected?: ChartRange | null
}

const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`
const cuft = (n: number) => `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)} cu ft`

function binLabel(bin: DistributionBin, measure: Measure, last: boolean): string {
  const f = measure === "price" ? money : cuft
  // The first bin starts at zero; it reads "Under $5,000", never "$0 to …" —
  // a "$0" in crawler text reads as a zero price (the HTML render gate flags it).
  if (bin.from <= 0) return `Under ${f(bin.to)}`
  return `${f(bin.from)} to ${last ? "" : "under "}${f(bin.to)}`
}

/** FRIDGE-1231: a column's x-axis label. The zero-start bin reads like the
 *  table ("Under $2,000", "Under 5 cu ft"); later edges stay compact. */
export function axisTickLabel(bin: DistributionBin, measure: Measure): string {
  if (bin.from <= 0) return `Under ${measure === "price" ? money(bin.to) : cuft(bin.to)}`
  return measure === "price" ? money(bin.from) : (bin.from % 1 === 0 ? bin.from.toFixed(0) : bin.from.toFixed(1))
}

/** 11 px system-UI text, measured: digits and lowercase average ~6.2 px. */
const textW = (s: string) => s.length * 6.2

/**
 * Choose which tick labels to draw and where: the smallest stride whose
 * labels, clamped inside the plot, keep an 8 px gap between neighbours.
 * The first label is always drawn (it carries the "Under …" wording).
 */
function placeLabels(labels: string[], xs: number[], left: number, right: number): Array<{ i: number; x: number }> {
  for (let every = 1; every <= Math.max(1, labels.length); every++) {
    const out: Array<{ i: number; x: number }> = []
    for (let i = 0; i < labels.length; i += every) {
      const half = textW(labels[i]) / 2
      out.push({ i, x: Math.min(Math.max(xs[i], left + half), right - half) })
    }
    const fits = out.every((p, k) => k === 0 || p.x - out[k - 1].x >= (textW(labels[out[k - 1].i]) + textW(labels[p.i])) / 2 + 8)
    if (fits) return out
  }
  return [{ i: 0, x: Math.min(Math.max(xs[0], left + textW(labels[0]) / 2), right - textW(labels[0]) / 2) }]
}

/** Sequential ramp (ordered bins): brand blue family, every step ≥ 3:1 on white. */
const RAMP_FROM = [76, 141, 245]
const RAMP_TO = [8, 51, 128]
function rampColor(i: number, n: number): string {
  const t = n <= 1 ? 1 : i / (n - 1)
  const c = RAMP_FROM.map((a, k) => Math.round(a + (RAMP_TO[k] - a) * t))
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

function arcPath(cx: number, cy: number, rOut: number, rIn: number, a0: number, a1: number): string {
  const sweep = a1 - a0
  if (sweep >= Math.PI * 2 - 1e-6) {
    // Full ring: two half arcs so the path is well-formed.
    return `M${cx},${cy - rOut} A${rOut},${rOut} 0 1 1 ${cx},${cy + rOut} A${rOut},${rOut} 0 1 1 ${cx},${cy - rOut} Z M${cx},${cy - rIn} A${rIn},${rIn} 0 1 0 ${cx},${cy + rIn} A${rIn},${rIn} 0 1 0 ${cx},${cy - rIn} Z`
  }
  const large = sweep > Math.PI ? 1 : 0
  const p = (r: number, a: number) => `${(cx + r * Math.sin(a)).toFixed(2)},${(cy - r * Math.cos(a)).toFixed(2)}`
  return `M${p(rOut, a0)} A${rOut},${rOut} 0 ${large} 1 ${p(rOut, a1)} L${p(rIn, a1)} A${rIn},${rIn} 0 ${large} 0 ${p(rIn, a0)} Z`
}

function niceTicks(max: number): number[] {
  if (max <= 0) return [0]
  const raw = max / 4
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 5, 10].map((s) => s * mag).find((w) => w >= raw) ?? 10 * mag
  const ticks: number[] = []
  for (let t = 0; t <= max + 1e-9; t += step) ticks.push(t)
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step)
  return ticks
}

export function CollectionDistributionChart({ title, priceBins, capacityBins, totalModels, initialChartType = "columns", initialMeasure, onSelectRange, selected = null }: Props) {
  const measures: Measure[] = []
  if (priceBins.length >= 2) measures.push("price")
  if (capacityBins.length >= 2) measures.push("capacity")
  const [measure, setMeasure] = React.useState<Measure>(initialMeasure ?? measures[0] ?? "price")
  const [chartType, setChartType] = React.useState<ChartType>(initialChartType)
  const [active, setActive] = React.useState<number | null>(null)
  const [pinned, setPinned] = React.useState<number | null>(null)
  const [width, setWidth] = React.useState(DEFAULT_W)
  const barRefs = React.useRef<Array<SVGRectElement | null>>([])
  const wrapRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0]?.contentRect.width ?? 0)
      if (w > 0) setWidth(Math.max(MIN_W, w))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  if (measures.length === 0) return null
  const VB_W = width

  const current: Measure = measures.includes(measure) ? measure : measures[0]
  const bins = current === "price" ? priceBins : capacityBins
  const total = bins.reduce((n, b) => n + b.count, 0)
  const maxCount = Math.max(...bins.map((b) => b.count))
  const ticks = niceTicks(maxCount)
  const yMax = ticks[ticks.length - 1] || 1
  const plotW = VB_W - PAD_L - PAD_R
  const plotH = VB_H - PAD_T - PAD_B
  const slot = plotW / bins.length
  const barW = Math.min(MAX_BAR, slot - GAP * 2)
  const y = (v: number) => PAD_T + plotH - (v / yMax) * plotH
  const tallest = bins.findIndex((b) => b.count === maxCount)
  // Axis labels never collide or clip: placed from their estimated width
  // (the table and the tooltip carry every value).
  const colLabels = placeLabels(
    bins.map((b) => axisTickLabel(b, current)),
    bins.map((_, i) => PAD_L + slot * i + slot / 2),
    2, VB_W - 2,
  )
  const f = current === "price" ? money : cuft
  // Cumulative view: running totals, one point per bin at its upper edge.
  const cum = bins.reduce<number[]>((acc, b) => { acc.push((acc[acc.length - 1] ?? 0) + b.count); return acc }, [])
  const cx0 = PAD_L + 8
  const cxStep = (plotW - 16) / bins.length
  const pointX = (i: number) => cx0 + cxStep * (i + 1)
  const yPct = (pct: number) => PAD_T + plotH - (pct / 100) * plotH
  const cumLabels = placeLabels(bins.map((b) => f(b.to)), bins.map((_, i) => pointX(i)), 2, VB_W - 2)
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0)
  const cumLabel = (i: number) => `${i === bins.length - 1 ? "up to" : "under"} ${f(bins[i].to)}`
  // When the chart drives a filter, the pinned tooltip follows the applied
  // range: a filter cleared outside the chart (its chip) unpins it too.
  const shown = active ?? (onSelectRange && !selected ? null : pinned)
  const measureLabel = current === "price" ? "Lowest tracked price per model" : "Capacity"
  const unit = current === "price" ? "price" : "capacity"

  // The Cumulative view's point i stands for everything at or below its edge.
  const rangeFrom = (i: number) => (chartType === "cumulative" ? bins[0].from : bins[i].from)
  const isSelected = (i: number) => !!selected && selected.measure === current && bins[i] !== undefined && selected.from === rangeFrom(i) && selected.to === bins[i].to
  // FRIDGE-1278 — choosing a section filters the listing (or clears it when
  // chosen again); without a listing to filter it only pins the tooltip.
  const choose = (i: number) => {
    if (!onSelectRange) { setPinned(pinned === i ? null : i); return }
    const same = isSelected(i)
    setPinned(same ? null : i)
    const b = bins[i]
    const last = i === bins.length - 1
    const label = chartType === "cumulative" ? `${last ? "Up to" : "Under"} ${f(b.to)}` : binLabel(b, current, last)
    onSelectRange(same ? null : { measure: current, from: rangeFrom(i), to: b.to, last, label })
  }
  const sectionName = (i: number) => `${binLabel(bins[i], current, i === bins.length - 1)}: ${bins[i].count.toLocaleString("en-US")} ${bins[i].count === 1 ? "model" : "models"}${onSelectRange ? (isSelected(i) ? " (showing these below; choose again to clear)" : " (show these below)") : ""}`

  const onKey = (i: number) => (e: React.KeyboardEvent<SVGRectElement>) => {
    let next: number | null = null
    if (e.key === "ArrowRight") next = Math.min(bins.length - 1, i + 1)
    if (e.key === "ArrowLeft") next = Math.max(0, i - 1)
    if (e.key === "Home") next = 0
    if (e.key === "End") next = bins.length - 1
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(i); return }
    if (next !== null) {
      e.preventDefault()
      // The donut draws no segment for an empty range, so step past missing
      // targets in the direction of travel (FRIDGE-1231).
      const dir = next >= i ? 1 : -1
      let j = next
      while (j >= 0 && j < bins.length && !barRefs.current[j]) j += dir
      if (j < 0 || j >= bins.length) {
        j = next
        while (j >= 0 && j < bins.length && !barRefs.current[j]) j -= dir
      }
      barRefs.current[j]?.focus()
    }
  }

  return (
    <div className="mt-5 border-t border-gray-100 pt-4" data-collection-chart>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* FRIDGE-1278 — the heading names its population: the band counts every
            tracked model, the chart only those with a price (or a capacity). */}
        <p className="m-0 text-[12px] font-bold uppercase tracking-[0.08em] text-gray-500">
          How the {total.toLocaleString("en-US")} models {current === "price" ? "with a tracked price" : "with a recorded capacity"} spread by {unit}
          {total < totalModels ? <span className="font-semibold normal-case tracking-normal text-gray-500"> · of {totalModels.toLocaleString("en-US")} tracked</span> : null}
        </p>
        <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Chart type" className="inline-flex rounded-full border border-gray-200 bg-white p-0.5">
          {CHART_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={chartType === t.id}
              onClick={() => { barRefs.current = []; setChartType(t.id); setActive(null); setPinned(null) }}
              className={`rounded-full px-3 py-1 text-[12px] font-bold transition-colors focus-visible:outline-2 focus-visible:outline-[#0469f0] ${chartType === t.id ? "bg-[#0469f0] text-white" : "text-gray-700 hover:bg-gray-100"}`}
            >{t.label}</button>
          ))}
        </div>
        {measures.length > 1 && (
          <div role="group" aria-label="Chart measure" className="inline-flex rounded-full border border-gray-200 bg-white p-0.5">
            {measures.map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={current === m}
                onClick={() => { setMeasure(m); setActive(null); setPinned(null) }}
                className={`rounded-full px-3 py-1 text-[12px] font-bold transition-colors focus-visible:outline-2 focus-visible:outline-[#0469f0] ${current === m ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100"}`}
              >
                {m === "price" ? "Price" : "Capacity"}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      <div className="relative" ref={wrapRef}>
        {chartType === "columns" && (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          width={VB_W}
          height={VB_H}
          role="group"
          aria-label={`${title}: number of models in each ${unit} range; ${bins.length} ranges from ${bins[0].from <= 0 ? "under " + (current === "price" ? money : cuft)(bins[0].to) : (current === "price" ? money : cuft)(bins[0].from)} to ${(current === "price" ? money : cuft)(bins[bins.length - 1].to)}`}
          className="block max-w-full"
          onMouseLeave={() => setActive(null)}
        >
          <desc>{`${title} — models by ${unit}`}</desc>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD_L} x2={VB_W - PAD_R} y1={y(t)} y2={y(t)} stroke={RULE} strokeWidth={1} />
              <text x={PAD_L - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>
                {t.toLocaleString("en-US")}
              </text>
            </g>
          ))}
          {bins.map((b, i) => {
            const cx = PAD_L + slot * i + slot / 2
            const h = (b.count / yMax) * plotH
            const top = y(b.count)
            const isOn = shown === i
            const r = Math.min(4, h / 2)
            // Rounded cap, square baseline: a path, not rx on the whole rect.
            const d = h <= 0 ? "" : `M${cx - barW / 2},${top + h} V${top + r} a${r},${r} 0 0 1 ${r},-${r} H${cx + barW / 2 - r} a${r},${r} 0 0 1 ${r},${r} V${top + h} Z`
            return (
              <g key={i}>
                {d && <path d={d} fill={isOn ? BAR_HOVER : BAR} />}
                {i === tallest && b.count > 0 && (
                  <text x={cx} y={top - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {b.count.toLocaleString("en-US")}
                  </text>
                )}
                {/* Hit target: the whole slot, taller than the bar, never only the painted pixels. */}
                <rect
                  ref={(el) => { barRefs.current[i] = el }}
                  x={PAD_L + slot * i}
                  y={PAD_T - 8}
                  width={slot}
                  height={plotH + 8}
                  fill="transparent"
                  tabIndex={0}
                  role="button"
                  aria-pressed={onSelectRange ? isSelected(i) : pinned === i}
                  aria-label={`${b.count.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} models, ${binLabel(b, current, i === bins.length - 1)}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  onClick={() => choose(i)}
                  onKeyDown={onKey(i)}
                  style={{ cursor: "pointer", outline: "none" }}
                />
                {isOn && (
                  <rect x={PAD_L + slot * i + 1} y={PAD_T - 8} width={slot - 2} height={plotH + 8} fill="none" stroke={BAR_HOVER} strokeWidth={1.5} rx={4} pointerEvents="none" />
                )}
              </g>
            )
          })}
          {colLabels.map((l) => (
            <text key={`x${l.i}`} data-axis="x" x={l.x} y={VB_H - PAD_B + 16} textAnchor="middle" fontSize={11} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>
              {axisTickLabel(bins[l.i], current)}
            </text>
          ))}
          <line x1={PAD_L} x2={VB_W - PAD_R} y1={y(0)} y2={y(0)} stroke="#cfd6e0" strokeWidth={1} />
          <text x={VB_W - PAD_R} y={VB_H - 4} textAnchor="end" fontSize={11} fill={MUTED}>{measureLabel}</text>
        </svg>
        )}

        {chartType === "donut" && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <svg
              viewBox="0 0 200 200"
              width={200}
              height={200}
              role="group"
              aria-label={`${title}: share of models in each ${unit} range; ${bins.length} ranges`}
              className="block shrink-0"
              onMouseLeave={() => setActive(null)}
            >
              <desc>{`${title} — share of models by ${unit}`}</desc>
              {(() => {
                let a = 0
                return bins.map((b, i) => {
                  const sweep = total > 0 ? (b.count / total) * Math.PI * 2 : 0
                  const a0 = a
                  a += sweep
                  if (sweep <= 0) return null
                  const isOn = shown === i
                  return (
                    <path
                      key={i}
                      data-donut-segment
                      ref={(el) => { barRefs.current[i] = el as unknown as SVGRectElement }}
                      d={arcPath(100, 100, isOn ? 96 : 90, 56, a0, a0 + sweep)}
                      fill={rampColor(i, bins.length)}
                      stroke="#ffffff"
                      strokeWidth={2}
                      tabIndex={0}
                      role="button"
                      aria-pressed={onSelectRange ? isSelected(i) : pinned === i}
                      aria-label={`${b.count.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} models (${pct(b.count)}%), ${binLabel(b, current, i === bins.length - 1)}`}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      onBlur={() => setActive(null)}
                      onClick={() => choose(i)}
                      onKeyDown={onKey(i) as unknown as React.KeyboardEventHandler<SVGPathElement>}
                      style={{ cursor: "pointer", outline: "none" }}
                    />
                  )
                })
              })()}
              <text x={100} y={98} textAnchor="middle" fontSize={20} fontWeight={800} fill={INK} style={{ fontVariantNumeric: "tabular-nums" }}>
                {total.toLocaleString("en-US")}
              </text>
              <text x={100} y={116} textAnchor="middle" fontSize={11} fill={MUTED}>models</text>
            </svg>
            <ul className="m-0 grid list-none gap-1 p-0 text-[12px] text-gray-700" aria-hidden={onSelectRange ? undefined : true}>
              {bins.map((b, i) => {
                const row = (
                  <>
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: rampColor(i, bins.length) }} />
                    <span>{binLabel(b, current, i === bins.length - 1)}</span>
                    <span className="font-semibold text-gray-900">{pct(b.count)}%</span>
                  </>
                )
                return (
                  <li key={i} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {onSelectRange && b.count > 0 ? (
                      <button type="button" onClick={() => choose(i)} aria-pressed={isSelected(i)} aria-label={sectionName(i)}
                        className={`flex min-h-[28px] w-full items-center gap-2 rounded-md px-1 text-left hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-[#0469f0] ${isSelected(i) ? "bg-[#0469f0]/10 font-semibold" : ""}`}>
                        {row}
                      </button>
                    ) : <span className="flex items-center gap-2 px-1">{row}</span>}
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {chartType === "cumulative" && (
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            width={VB_W}
            height={VB_H}
            role="group"
            aria-label={`${title}: share of models at or below each ${unit}; ${bins.length} points, ending at 100%`}
            className="block max-w-full"
            onMouseLeave={() => setActive(null)}
          >
            <desc>{`${title} — cumulative share of models by ${unit}`}</desc>
            {[0, 25, 50, 75, 100].map((t) => (
              <g key={t}>
                <line x1={PAD_L} x2={VB_W - PAD_R} y1={yPct(t)} y2={yPct(t)} stroke={RULE} strokeWidth={1} />
                <text x={PAD_L - 6} y={yPct(t) + 4} textAnchor="end" fontSize={11} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>{t}%</text>
              </g>
            ))}
            <path
              d={`M${cx0},${yPct(0)} ${cum.map((c, i) => `L${pointX(i).toFixed(2)},${yPct(pct(c)).toFixed(2)}`).join(" ")}`}
              fill="none"
              stroke={BAR}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {cum.map((c, i) => {
              const isOn = shown === i
              return (
                <g key={i}>
                  <circle cx={pointX(i)} cy={yPct(pct(c))} r={isOn ? 5 : 3.5} fill={isOn ? BAR_HOVER : "#ffffff"} stroke={BAR} strokeWidth={2} />
                  <rect
                    data-cumulative-point
                    ref={(el) => { barRefs.current[i] = el }}
                    x={pointX(i) - cxStep / 2}
                    y={PAD_T - 8}
                    width={cxStep}
                    height={plotH + 8}
                    fill="transparent"
                    tabIndex={0}
                    role="button"
                    aria-pressed={onSelectRange ? isSelected(i) : pinned === i}
                    aria-label={`${c.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} models (${pct(c)}%) ${cumLabel(i)}`}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onBlur={() => setActive(null)}
                    onClick={() => choose(i)}
                    onKeyDown={onKey(i)}
                    style={{ cursor: "pointer", outline: "none" }}
                  />
                </g>
              )
            })}
            {cumLabels.map((l) => (
              <text key={`c${l.i}`} data-axis="x" x={l.x} y={VB_H - PAD_B + 16} textAnchor="middle" fontSize={11} fill={MUTED} style={{ fontVariantNumeric: "tabular-nums" }}>
                {f(bins[l.i].to)}
              </text>
            ))}
            <line x1={PAD_L} x2={VB_W - PAD_R} y1={yPct(0)} y2={yPct(0)} stroke="#cfd6e0" strokeWidth={1} />
            <text x={VB_W - PAD_R} y={VB_H - 4} textAnchor="end" fontSize={11} fill={MUTED}>{`Share of models at or below this ${unit}`}</text>
          </svg>
        )}

        {shown !== null && bins[shown] && (
          <div
            role="status"
            className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] leading-snug text-gray-700 shadow-sm"
          >
            {chartType === "cumulative" ? (
              <>
                <span className="block text-[15px] font-black text-gray-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {pct(cum[shown])}%
                  <span className="ml-1 text-[12px] font-semibold text-gray-500">{cum[shown].toLocaleString("en-US")} {cum[shown] === 1 ? "model" : "models"}</span>
                </span>
                {cumLabel(shown)}
              </>
            ) : (
              <>
                <span className="block text-[15px] font-black text-gray-900" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {bins[shown].count.toLocaleString("en-US")} {bins[shown].count === 1 ? "model" : "models"}
                  <span className="ml-1 text-[12px] font-semibold text-gray-500">{total > 0 ? `${pct(bins[shown].count)}%` : ""}</span>
                </span>
                {binLabel(bins[shown], current, shown === bins.length - 1)}
              </>
            )}
          </div>
        )}
      </div>

      {onSelectRange ? (
        <p className="mt-2 mb-0 text-[11px] text-gray-500">Click or tap a section to list those models below; choose it again to clear.</p>
      ) : null}
      <details className="mt-2 text-[12px] text-gray-600">
        <summary className="cursor-pointer font-semibold text-gray-700">View as a table</summary>
        <table className="mt-2 w-full text-left" style={{ fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr className="border-b border-gray-200 text-[11px] uppercase tracking-[0.08em] text-gray-500">
              <th scope="col" className="py-1 pr-3 font-bold">{current === "price" ? "Price range" : "Capacity range"}</th>
              <th scope="col" className="py-1 pr-3 font-bold">Models</th>
              <th scope="col" className="py-1 font-bold">Share</th>
            </tr>
          </thead>
          <tbody>
            {bins.map((b, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-b-0">
                <td className="py-1 pr-3">
                  {onSelectRange && b.count > 0 ? (
                    <button type="button" onClick={() => choose(i)} aria-pressed={isSelected(i)} className="text-left font-semibold text-[#0469f0] underline-offset-2 hover:underline">
                      {binLabel(b, current, i === bins.length - 1)}
                    </button>
                  ) : binLabel(b, current, i === bins.length - 1)}
                </td>
                <td className="py-1 pr-3">{b.count.toLocaleString("en-US")}</td>
                <td className="py-1">{total > 0 ? `${Math.round((b.count / total) * 100)}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-1 text-[11px] text-gray-500">
          {totalModels.toLocaleString("en-US")} models tracked; {total.toLocaleString("en-US")} carry a {unit}.
        </p>
      </details>
    </div>
  )
}
